import { useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../../api';
import { NavIcon } from '../../shell/nav-icon';
import type { AnalyticsResult } from '../../mock/seeds/schema-types';
import { formatMonthYear } from '@bookingdei/i18n/format-date';
import type { Fmt, KpiCard } from './overview-model';
import { filledSegments, num, rateLabel, sparkPaths, splitNumber } from './overview-model';

// One Overview KPI card (design D:633-686, styles `overviewVals` D:9086-9134): icon +
// label, the figure with its side note, the month-on-month chip, the breakdown row, the
// trend word and a 7-point sparkline. The header is a disclosure: it expands the card
// to the KPI's own monthly series, read from `GET /admin/analytics?kpi=` (API-207) —
// the design spec's "expands to a breakdown list on click".
const RATE_PCT = { strong: 90, steady: 60, down: 30, flat: 40 } as const;

type Breakdown =
  { state: 'loading' } | { state: 'error' } | { state: 'ready'; items: AnalyticsResult['items'] };

export function KpiCardView({ card, index, fmt }: { card: KpiCard; index: number; fmt: Fmt }) {
  const hero = index === 0;
  const [open, setOpen] = useState(false);
  const [breakdown, setBreakdown] = useState<Breakdown | null>(null);
  const num0 = splitNumber(card.value);
  const numClass =
    num0.main.length > 9 ? 'ov-kpi__num--xs' : num0.main.length > 5 ? 'ov-kpi__num--sm' : '';
  const sp = card.spark.length > 1 ? sparkPaths(card.spark) : null;
  const panelId = `ov-kpi-${card.id}-breakdown`;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && (!breakdown || breakdown.state === 'error')) {
      setBreakdown({ state: 'loading' });
      (
        api.GET('/admin/analytics', {
          params: { query: { scope: 'overview', kpi: card.id } },
        }) as Promise<AnalyticsResult>
      )
        .then((r) =>
          setBreakdown({
            state: 'ready',
            items: [...(r.items ?? [])].sort((a, b) => b.day.localeCompare(a.day)),
          }),
        )
        .catch(() => setBreakdown({ state: 'error' }));
    }
  };

  const hueClass = hero ? 'ov-kpi--hero' : `ov-kpi--h${((((index - 1) % 4) + 4) % 4) + 1}`;
  return (
    <div className={`ov-kpi ${hueClass}`}>
      <button
        type="button"
        className="ov-kpi__head"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
      >
        <span className="ov-kpi__icon" aria-hidden="true">
          <NavIcon name={card.icon} size={17} />
        </span>
        <span className="ov-kpi__label">{card.label}</span>
      </button>
      <div className="ov-kpi__figure">
        {num0.pre && <span className={`ov-kpi__num ${numClass}`}>{num0.pre}</span>}
        <div className={`ov-kpi__num ${numClass}`}>{num0.main}</div>
        {num0.suf && <span className="ov-kpi__suf">{num0.suf}</span>}
        {card.sideV && (
          <div className="ov-kpi__side">
            <div
              className={
                card.sideV.length > 6 ? 'ov-kpi__side-v ov-kpi__side-v--sm' : 'ov-kpi__side-v'
              }
            >
              {card.sideV}
            </div>
            <div className="ov-kpi__side-s">{card.sideS}</div>
          </div>
        )}
      </div>
      {card.delta && (
        <div className="ov-kpi__delta-row">
          <span className={card.delta.up ? 'ov-kpi__delta' : 'ov-kpi__delta ov-kpi__delta--down'}>
            {card.delta.text}
          </span>
          {card.note && <span className="ov-kpi__note">{card.note}</span>}
        </div>
      )}
      {card.stats.length > 0 && (
        <div className="ov-kpi__stats" style={{ '--ov-cols': card.stats.length } as CSSProperties}>
          {card.stats.map((s) => (
            <div key={s.k} className="ov-kpi__stat">
              <div
                className={s.v.length > 9 ? 'ov-kpi__stat-v ov-kpi__stat-v--sm' : 'ov-kpi__stat-v'}
              >
                {s.v}
              </div>
              <div className="ov-kpi__stat-k">{s.k}</div>
              <div className="ov-kpi__stat-s">{s.s}</div>
            </div>
          ))}
        </div>
      )}
      {card.sub && <div className="ov-kpi__sub">{card.sub}</div>}
      <div className="ov-kpi__rate">
        <span className="ov-kpi__rate-k">{fmt.t('admin.overview.trend')}</span>
        <span className={`ov-kpi__rate-w ov-kpi__rate-w--${card.rate}`}>
          {rateLabel(fmt, card.rate)}
        </span>
      </div>
      {sp ? (
        <svg
          className="ov-kpi__spark"
          viewBox="0 0 100 40"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path className="ov-kpi__spark-area" d={sp.area} />
          <path className="ov-kpi__spark-line" d={sp.line} />
          <circle className="ov-kpi__spark-dot" cx={sp.dotX} cy={sp.dotY} r="3" />
        </svg>
      ) : (
        <div className="ov-kpi__segs" aria-hidden="true">
          {Array.from({ length: 5 }, (_, i) => (
            <span
              key={i}
              className={
                i < filledSegments(RATE_PCT[card.rate])
                  ? `ov-kpi__seg ov-kpi__seg--on-${card.rate}`
                  : 'ov-kpi__seg'
              }
            />
          ))}
        </div>
      )}
      <div id={panelId} className="ov-kpi__breakdown" hidden={!open}>
        {open && breakdown?.state === 'loading' && (
          <div className="ov-kpi__bd-note">{fmt.t('admin.overview.breakdown.loading')}</div>
        )}
        {open && breakdown?.state === 'error' && (
          <div className="ov-kpi__bd-note" role="alert">
            {fmt.t('admin.overview.breakdown.error')}
          </div>
        )}
        {open && breakdown?.state === 'ready' && breakdown.items.length === 0 && (
          <div className="ov-kpi__bd-note">{fmt.t('admin.overview.breakdown.empty')}</div>
        )}
        {open && breakdown?.state === 'ready' && breakdown.items.length > 0 && (
          <ul className="ov-kpi__bd-list" aria-label={card.label}>
            {breakdown.items.map((it) => (
              <li key={it.day} className="ov-kpi__bd-row">
                <span>{formatMonthYear(fmt.lang, new Date(`${it.day}T00:00:00`))}</span>
                <span className="ov-kpi__bd-v">
                  {card.money ? fmt.money(it.value) : num(fmt, it.value)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
